import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import type { TimeReportsV2Group } from "services/api/company/timeReports.service";
import { useWorkerAvatar } from "web-apps/company/components/common/hooks/useWorkerAvatar";
import type { TimeReportsV2DisplayOption } from "web-apps/company/stores/timeReportsV2/timeReportsV2Store.reducer";
import type { PersonDetailSelection } from "web-apps/company/components/timeReportsV2/components/views/person/models/personView.types";
import type { InlineTimeReportEditActivity } from "web-apps/company/components/timeReportsV2/components/views/person/hooks/useInlineTimeReportEdit";
import { usePersonRowViewModel } from "web-apps/company/components/timeReportsV2/components/views/person/hooks/usePersonRowViewModel";
import { getTagIconColor } from "./personRow.presentation";
import {
  PersonSummaryDetails,
  PersonSummaryLabels,
} from "./personSummaryDetails.component";
import { PersonTimeReportDetailedRow } from "./personTimeReportDetailedRow.component";
import { PersonTimeReportRow } from "./personTimeReportRow.component";
import { isZeroLikeValue } from "./personRow.mapper";
import { DetailsList, RowCard, SelectAllRow } from "./personSummaryRow.styled";

interface PersonRowProps {
  readonly companyId?: number;
  readonly deviationToleranceMinutes: number;
  readonly displayOption: TimeReportsV2DisplayOption;
  readonly includeFlexTimeDeviations: boolean;
  readonly isDetailOpeningDisabled?: boolean;
  readonly isExpandedByDefault: boolean;
  readonly isReadOnly: boolean;
  readonly row: TimeReportsV2Group;
  readonly selectedDetailIds: number[];
  readonly onDetailClick?: (selection: PersonDetailSelection) => void;
  readonly onDownloadClick?: (row: TimeReportsV2Group) => void;
  readonly onInlineEditPendingChange?: (
    activity: InlineTimeReportEditActivity,
  ) => void;
  readonly onInlineEditSaved: () => void;
  readonly onSelectionChange?: (
    selection: PersonDetailSelection[],
    checked: boolean,
  ) => void;
}

export const PersonRow = (props: PersonRowProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const isDetailed = props.displayOption === "detailed";
  const [isExpanded, setIsExpanded] = useState(props.isExpandedByDefault);
  const { onDetailClick, onSelectionChange, onDownloadClick, row } = props;

  const {
    teamLabel,
    summary,
    detailEntries,
    rowSelections,
    selectionByDetailId,
    selectedDetailIdSet,
    isAllSelected,
    isPartiallySelected,
  } = usePersonRowViewModel({
    row,
    deviationToleranceMinutes: props.deviationToleranceMinutes,
    includeFlexTimeDeviations: props.includeFlexTimeDeviations,
    selectedDetailIds: props.selectedDetailIds,
    locale: i18n.language,
    translate: t,
  });

  const avatarUrl = useWorkerAvatar({
    workerExternalId: row.externalId,
    isWorker: true,
    initialUrl: null,
  });

  const labels: PersonSummaryLabels = {
    shifts: t("TimeReports.Shifts"),
    plannedTime: t("TimeReports.PlannedTime"),
    approvedTime: t("TimeReports.ApprovedTime"),
    toApprove: t("TimeReports.ToApprove"),
    unreported: t("TimeReports.Unreported"),
    absence: t("TimeReports.Absence"),
    deviation: t("TimeReports.Deviation"),
    download: t("TimeReports.Download"),
    collapse: t("TimeReports.CollapseRow"),
    expand: t("TimeReports.ExpandRow"),
  };

  useEffect(() => {
    setIsExpanded(props.isExpandedByDefault);
  }, [props.isExpandedByDefault]);

  const handleDetailClick = useCallback(
    (detailId: number): void => {
      if (!onDetailClick) return;
      const selection = selectionByDetailId.get(detailId);
      if (!selection) return;
      onDetailClick(selection);
    },
    [onDetailClick, selectionByDetailId],
  );

  const handleSelectAllChange = useCallback(
    (checked: boolean): void => {
      onSelectionChange?.(rowSelections, checked);
    },
    [onSelectionChange, rowSelections],
  );

  const handleDownloadClick = useCallback((): void => {
    onDownloadClick?.(row);
  }, [onDownloadClick, row]);

  const handleDetailSelectionChange = useCallback(
    (detailId: number, checked: boolean): void => {
      const selection = selectionByDetailId.get(detailId);
      if (!selection) return;
      onSelectionChange?.([selection], checked);
    },
    [onSelectionChange, selectionByDetailId],
  );

  const detailRows = useMemo(
    () =>
      detailEntries.map((entry) => ({
        ...entry,
        onOpenDetails: (): void => {
          handleDetailClick(entry.selection.detail.id);
        },
        onToggleSelection: (checked: boolean): void => {
          handleDetailSelectionChange(entry.selection.detail.id, checked);
        },
      })),
    [detailEntries, handleDetailClick, handleDetailSelectionChange],
  );

  return (
    <RowCard>
      <PersonSummaryDetails
        name={row.name}
        role={row.role}
        teamLabel={teamLabel ?? t("TimeReports.TeamFallbackLabel")}
        avatarUrl={avatarUrl}
        summary={summary}
        isDetailed={isDetailed}
        isExpanded={isExpanded}
        isZeroLikeValue={isZeroLikeValue}
        showDeviation={props.includeFlexTimeDeviations}
        labels={labels}
        onDownloadClick={handleDownloadClick}
        onToggleExpand={() => setIsExpanded((value) => !value)}
      />

      {isExpanded && (
        <DetailsList>
          <SelectAllRow $isDetailed={isDetailed}>
            <Checkbox
              label={t("CompanyTimeReport.SelectAll")}
              checked={
                isAllSelected
                  ? "checked"
                  : isPartiallySelected
                    ? "indeterminate"
                    : "unchecked"
              }
              onChange={handleSelectAllChange}
            />
          </SelectAllRow>
          {detailRows.map((entry) => {
            return isDetailed ? (
              <PersonTimeReportDetailedRow
                key={entry.selection.detail.id}
                item={entry.selection.item}
                detailRow={entry.selection.detail}
                companyId={props.companyId}
                isReadOnly={props.isReadOnly}
                hasDeviation={props.includeFlexTimeDeviations}
                onSaved={props.onInlineEditSaved}
                onPendingChange={props.onInlineEditPendingChange}
                isDetailOpeningDisabled={props.isDetailOpeningDisabled}
                updatedAtLabel={entry.updatedAtLabel}
                isSelected={selectedDetailIdSet.has(entry.selection.detail.id)}
                onOpenDetails={entry.onOpenDetails}
                onToggleSelection={entry.onToggleSelection}
              />
            ) : (
              <PersonTimeReportRow
                key={entry.selection.detail.id}
                detailRow={entry.selection.detail}
                updatedAtLabel={entry.updatedAtLabel}
                isSelected={selectedDetailIdSet.has(entry.selection.detail.id)}
                getTagIconColor={getTagIconColor}
                onOpenDetails={entry.onOpenDetails}
                onToggleSelection={entry.onToggleSelection}
              />
            );
          })}
        </DetailsList>
      )}
    </RowCard>
  );
};
